import { useState, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import type {
  AddEditClinicianRequestT,
  DoseSpotClinicianInfoT,
  ErxRegistrationStatusTypeT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { useAuth } from '@/app/providers';
import { useMyIdentificationQuery } from '@/entities/identification';
import { useMyEmployeeRecordQuery } from '@/entities/me';
import { maskNpiInput } from '@/shared/lib/formatters';
import { commitOrPushErrors } from '@/shared/lib/forms';
import { FormField } from '@/shared/ui/primitives';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { FieldGroup } from '@/shared/ui/shadcn/field';

import {
  LOTUS_OFFICE_DEFAULTS,
  maskDobMdyInput,
  toDobIsoZ,
  usePrescriberDetailsForm,
} from '../../model/dosespot';
import type { PrescriberDetailsPayload } from '../../model/useClinicianOnboardingOrchestrator';
import { DS_STEP_FORM_ID } from './_shared/dsStepFormId';

export interface PrescriberDetailsStepProps {
  prefill?: DoseSpotClinicianInfoT | null;
  registrationError?: ErxRegistrationStatusTypeT | null;
  onSubmit: (payload: PrescriberDetailsPayload) => void;
  submitError: Error | null;
}

function prepareClinicianInfo(raw: AddEditClinicianRequestT): AddEditClinicianRequestT {
  return {
    ...raw,
    ...LOTUS_OFFICE_DEFAULTS,
    DateOfBirth: toDobIsoZ(raw.DateOfBirth),
  };
}

/**
 * Maps DoseSpot's `registrationStatus` error variants to user-facing copy.
 *
 * The destructive branch (`RegistrationError`) is by far the most common case
 * during onboarding -- it fires whenever DoseSpot's NPPES-backed validator
 * can't reconcile the submitted profile against the federal NPI registry.
 * In practice that almost always means the NPI doesn't match the provided
 * name (or, less often, the supporting credentials), so we name the registry
 * explicitly and link to it instead of asking the user to guess what
 * "registration error" means.
 *
 * The TFA variants are operationally distinct (post-IDP two-factor failures,
 * unrelated to NPI) and shouldn't reach this step in the onboarding flow,
 * but we surface a generic message for them defensively.
 */
function registrationErrorMessage(error: ErxRegistrationStatusTypeT): ReactNode {
  switch (error) {
    case 'RegistrationError':
      return (
        <>
          DoseSpot couldn&apos;t verify your prescriber profile. Your NPI must be officially
          registered to the name (and credentials) you submitted in{' '}
          <a
            href="https://npiregistry.cms.hhs.gov"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline underline-offset-2 hover:no-underline"
          >
            NPPES, the federal NPI registry
          </a>
          . Please double-check your NPI number and confirm the name and date of birth above match
          what&apos;s publicly listed for it, then try again.
        </>
      );
    case 'TFAActivatedError':
    case 'TFADeactivatedError':
      return 'DoseSpot couldn’t update two-factor authentication on your account. Please review your details and try again.';
    default:
      return 'DoseSpot returned a registration error. Please review your details and try again.';
  }
}

export function PrescriberDetailsStep(props: PrescriberDetailsStepProps) {
  const { prefill, registrationError, onSubmit, submitError } = props;
  const { isAuthenticated } = useAuth();
  const identificationQuery = useMyIdentificationQuery();
  const employeeRecordQuery = useMyEmployeeRecordQuery();

  const { form, validateDraft } = usePrescriberDetailsForm({
    prefill,
    employeeRecord: employeeRecordQuery.data,
  });
  const backendUserId = identificationQuery.data?.userId;

  const defaults = form.state.values.clinicianInfo;
  const hasUsablePrefill = defaults.FirstName.trim() !== '' && defaults.LastName.trim() !== '';

  const [editing, setEditing] = useState(!hasUsablePrefill || registrationError != null);

  const handleSubmit = () => {
    if (!isAuthenticated || backendUserId === undefined) {
      return;
    }

    const validated = commitOrPushErrors(form, validateDraft());
    if (!validated) {
      setEditing(true);
      return;
    }

    const clinicianData = prepareClinicianInfo({
      ...validated.clinicianInfo,
      ClinicianRoleType: ['1'],
    });
    onSubmit({ clinicianInfo: clinicianData, userId: backendUserId });
  };

  return (
    <div className="flex flex-col gap-4">
      {registrationError ? (
        <Alert variant="destructive">
          <AlertDescription>{registrationErrorMessage(registrationError)}</AlertDescription>
        </Alert>
      ) : null}

      {submitError ? (
        <Alert variant="destructive">
          <AlertDescription>{submitError.message}</AlertDescription>
        </Alert>
      ) : null}

      {editing ? (
        <form
          id={DS_STEP_FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <FieldGroup>
            <FormField
              form={form}
              type="text"
              name="clinicianInfo.FirstName"
              label="First name"
              inputProps={{ autoComplete: 'given-name' }}
            />

            <FormField
              form={form}
              type="text"
              name="clinicianInfo.LastName"
              label="Last name"
              inputProps={{ autoComplete: 'family-name' }}
            />

            <FormField
              form={form}
              type="text"
              name="clinicianInfo.DateOfBirth"
              label="Date of birth"
              placeholder="MM/DD/YYYY"
              parse={maskDobMdyInput}
              inputProps={{
                autoComplete: 'bday',
                inputMode: 'numeric',
                maxLength: 10,
              }}
            />

            <FormField
              form={form}
              type="text"
              name="clinicianInfo.NPINumber"
              label="NPI number"
              placeholder="10-digit NPI"
              parse={maskNpiInput}
              inputProps={{ autoComplete: 'off', inputMode: 'numeric', maxLength: 10 }}
            />
          </FieldGroup>
        </form>
      ) : (
        <PrescriberCard
          firstName={form.state.values.clinicianInfo.FirstName}
          lastName={form.state.values.clinicianInfo.LastName}
          dateOfBirth={form.state.values.clinicianInfo.DateOfBirth}
          npi={form.state.values.clinicianInfo.NPINumber}
          onEdit={() => setEditing(true)}
          formId={DS_STEP_FORM_ID}
          onConfirm={handleSubmit}
        />
      )}
    </div>
  );
}

interface PrescriberCardProps {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  npi: string;
  onEdit: () => void;
  formId: string;
  onConfirm: () => void;
}

function getInitials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function PrescriberCard({
  firstName,
  lastName,
  dateOfBirth,
  npi,
  onEdit,
  formId,
  onConfirm,
}: PrescriberCardProps) {
  return (
    <>
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-px shadow-lg backdrop-blur-xl dark:border-white/[0.08]">
        <div className="flex flex-col gap-5 rounded-[calc(1rem-1px)] p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary dark:bg-white/10 dark:text-white">
                {getInitials(firstName, lastName)}
              </div>
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold leading-tight">
                  {firstName} {lastName}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">Prescriber</p>
              </div>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0 gap-1.5 rounded-full text-xs text-muted-foreground hover:text-foreground"
              onClick={onEdit}
            >
              <Pencil className="size-3" />
              Edit
            </Button>
          </div>

          <div className="h-px bg-border/50" />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
                Date of Birth
              </p>
              <p className="mt-1 text-sm font-medium tabular-nums">{dateOfBirth || '—'}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
                NPI
              </p>
              <p className="mt-1 font-mono text-sm font-medium tracking-wide">{npi || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm();
        }}
        className="hidden"
      />
    </>
  );
}
